import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Avatar, Badge, Button, Card, Field, IconBtn, Input, Spinner, Tabs } from "../../design-system";
import { Icon, SupSection, SupSelect, CategoryIcon, AssetStatusBadge, StatusBadge } from "./atoms";
import { ASTATUS } from "./data";
import { useSoporteNav } from "./nav";
import {
  getSupportAsset, setSupportAssetStatus, assignSupportAsset, returnSupportAsset,
  addSupportAssetMaintenance, getEmployees,
} from "../../helpers/kronaly_helper";

const unwrap = (r) => r?.data ?? r;
const empName = (e) => [e?.first_name, e?.last_name].filter(Boolean).join(" ").trim() || "Sin nombre";
const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }) : "—");

function SoporteActivo() {
  const nav = useSoporteNav();
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("resumen");
  const [panel, setPanel] = useState(null); // 'assign' | 'return' | null

  const load = useCallback(() => {
    setLoading(true);
    return getSupportAsset(id)
      .then((r) => { setA(unwrap(r)); setError(null); })
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (a?.code) document.title = `${a.code} · Inventario | Kronaly`; }, [a]);

  const act = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); setPanel(null); await load(); }
    catch (e) { setError(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  if (loading && !a) return <div className="kr-page"><div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}><Spinner size={20} /></div></div>;
  if (error && !a) return <div className="kr-page"><Alert tone="danger" title="No se pudo cargar el activo">{error}</Alert></div>;
  if (!a) return null;

  const warrantyExpired = a.warranty_until && new Date(a.warranty_until) < new Date();

  return (
    <div className="kr-page">
      <button onClick={() => nav.back()} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: "var(--kr-fg-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, alignSelf: "flex-start" }}>
        <Icon name="arrowLeft" size={14} />Volver al inventario
      </button>

      {error && <Alert tone="danger" title="Error">{error}</Alert>}

      <Card>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <CategoryIcon icon={a.type?.icon} color="neutral" size={56} iconSize={26} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-muted)", fontWeight: 600 }}>{a.code}</span>
              <AssetStatusBadge status={a.status} />
            </div>
            <h1 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>{[a.brand, a.model].filter(Boolean).join(" ") || a.code}</h1>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 10, fontSize: 12, color: "var(--kr-fg-muted)" }}>
              {a.type && <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="package" size={13} />{a.type.name}</span>}
              {a.location && <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="mapPin" size={13} />{a.location}</span>}
              {a.current_assignment && <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="users" size={13} />{a.current_assignment.employee_name}</span>}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <SupSelect value={a.status} onChange={(s) => { if (s !== a.status) act(() => setSupportAssetStatus(id, s)); }} options={Object.keys(ASTATUS).map((s) => ({ value: s, label: ASTATUS[s].label }))} style={{ width: 190 }} />
            {a.current_employee_profile_id
              ? <Button variant="secondary" size="sm" icon="RefreshCw" onClick={() => setPanel(panel === "return" ? null : "return")}>Devolución</Button>
              : <Button variant="primary" size="sm" icon="UserPlus" onClick={() => setPanel(panel === "assign" ? null : "assign")}>Asignar</Button>}
          </div>
        </div>

        {panel === "assign" && <AssignPanel busy={busy} onCancel={() => setPanel(null)} onSubmit={(d) => act(() => assignSupportAsset(id, d))} />}
        {panel === "return" && <ReturnPanel busy={busy} onCancel={() => setPanel(null)} onSubmit={(d) => act(() => returnSupportAsset(id, d))} />}
      </Card>

      <Card padded={false}>
        <div style={{ padding: "8px 20px 0", overflowX: "auto" }}>
          <Tabs value={tab} onChange={setTab} tabs={[
            { id: "resumen", label: "Resumen" },
            { id: "asignacion", label: "Asignación" },
            { id: "historico", label: "Histórico", count: (a.assignments || []).length },
            { id: "tickets", label: "Tickets", count: (a.tickets || []).length },
            { id: "mantenimiento", label: "Mantenimientos", count: (a.maintenances || []).length },
            { id: "documentos", label: "Documentos", count: (a.documents || []).length },
            { id: "auditoria", label: "Auditoría" },
          ]} />
        </div>
        <div style={{ padding: 24, background: "var(--kr-neutral-50)" }}>
          {tab === "resumen" && <Resumen a={a} warrantyExpired={warrantyExpired} />}
          {tab === "asignacion" && <Asignacion a={a} onAssign={() => setPanel("assign")} onReturn={() => setPanel("return")} />}
          {tab === "historico" && <Historico rows={a.assignments || []} />}
          {tab === "tickets" && <TicketsTab rows={a.tickets || []} nav={nav} />}
          {tab === "mantenimiento" && <Mantenimientos rows={a.maintenances || []} busy={busy} onAdd={(d) => act(() => addSupportAssetMaintenance(id, d))} />}
          {tab === "documentos" && <Documentos rows={a.documents || []} />}
          {tab === "auditoria" && <Auditoria rows={a.audit || []} />}
        </div>
      </Card>
    </div>
  );
}

function KV({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 500, fontFamily: mono ? "var(--kr-font-mono)" : "inherit" }}>{value ?? "—"}</div>
    </div>
  );
}

function Resumen({ a, warrantyExpired }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card>
        <SupSection eyebrow="Identificación" title="Datos del equipo" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginTop: 16 }} data-responsive-grid="3">
          <KV label="Código interno" value={a.code} mono />
          <KV label="Tipo" value={a.type?.name} />
          <KV label="Estado" value={<AssetStatusBadge status={a.status} />} />
          <KV label="Marca" value={a.brand} />
          <KV label="Modelo" value={a.model} />
          <KV label="Nº de serie" value={a.serial} mono />
          <KV label="Usuario asignado" value={a.current_assignment?.employee_name} />
          <KV label="Ubicación" value={a.location} />
        </div>
      </Card>
      <Card>
        <SupSection eyebrow="Compra y garantía" title="Adquisición" />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginTop: 16 }} data-responsive-grid="4">
          <KV label="Fecha de compra" value={a.purchased_on} mono />
          <KV label="Coste" value={a.cost} mono />
          <KV label="Proveedor" value={a.provider} />
          <div>
            <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginBottom: 4 }}>Garantía hasta</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500, fontFamily: "var(--kr-font-mono)", color: warrantyExpired ? "var(--kr-danger-700)" : "var(--kr-fg-strong)" }}>{a.warranty_until || "—"}</span>
              {a.warranty_until && (warrantyExpired ? <Badge tone="danger">Caducada</Badge> : <Badge tone="success">Vigente</Badge>)}
            </div>
          </div>
        </div>
        {a.notes && <p style={{ fontSize: 13, color: "var(--kr-fg-primary)", lineHeight: 1.6, margin: "16px 0 0" }}>{a.notes}</p>}
      </Card>
    </div>
  );
}

function Asignacion({ a, onAssign, onReturn }) {
  const c = a.current_assignment;
  if (!c) return (
    <Card>
      <SupSection eyebrow="Asignación actual" title="Sin asignación activa" />
      <p style={{ fontSize: 13, color: "var(--kr-fg-muted)", margin: "8px 0 0" }}>Este equipo está <strong>{ASTATUS[a.status]?.label.toLowerCase()}</strong> y no tiene usuario asignado.</p>
      <div style={{ marginTop: 14 }}><Button variant="primary" size="sm" icon="UserPlus" onClick={onAssign}>Asignar a un usuario</Button></div>
    </Card>
  );
  return (
    <Card>
      <SupSection eyebrow="Asignación actual" title="Usuario que tiene el equipo" right={<Button variant="secondary" size="sm" icon="RefreshCw" onClick={onReturn}>Registrar devolución</Button>} />
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", border: "1px solid var(--kr-border-subtle)", borderRadius: 10, background: "var(--kr-neutral-25)", marginTop: 14 }}>
        <Avatar name={c.employee_name || "?"} size={44} />
        <div style={{ flex: 1 }}><div style={{ fontSize: 15, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{c.employee_name}</div></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginTop: 16 }} data-responsive-grid="3">
        <KV label="Fecha de entrega" value={c.assigned_on} mono />
        <KV label="Estado al entregar" value={c.delivered_state} />
        <KV label="Accesorios" value={c.accessories} />
      </div>
    </Card>
  );
}

function Historico({ rows }) {
  if (!rows.length) return <Card><p style={{ fontSize: 13, color: "var(--kr-fg-muted)", margin: 0 }}>Sin histórico de asignaciones.</p></Card>;
  return (
    <Card padded={false}>
      <div style={{ padding: "14px 18px" }}><SupSection eyebrow="Trazabilidad" title="Histórico de asignaciones" right={<Badge tone="info" variant="soft">El histórico vive en el activo</Badge>} /></div>
      <div data-scroll-x style={{ overflowX: "auto" }}>
        <div style={{ minWidth: 760 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1.1fr 1.1fr 1.2fr", gap: 10, padding: "10px 18px", background: "var(--kr-neutral-50)", borderTop: "1px solid var(--kr-border-subtle)", borderBottom: "1px solid var(--kr-border)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
            <span>Usuario</span><span>Entrega</span><span>Devolución</span><span>Estado entrega</span><span>Estado devol.</span><span>Accesorios</span>
          </div>
          {rows.map((h, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1.1fr 1.1fr 1.2fr", gap: 10, padding: "12px 18px", borderBottom: "1px solid var(--kr-border-subtle)", fontSize: 12, alignItems: "center", background: h.current ? "var(--kr-action-soft-bg)" : "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <Avatar name={h.employee_name || "?"} size={24} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "var(--kr-fg-strong)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.employee_name}</div>
                  {h.current && <div style={{ fontSize: 10, color: "var(--kr-action-soft-fg)", fontWeight: 600 }}>Actual</div>}
                </div>
              </div>
              <span style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)" }}>{h.assigned_on}</span>
              <span style={{ fontFamily: "var(--kr-font-mono)", color: h.returned_on ? "var(--kr-fg-strong)" : "var(--kr-fg-subtle)" }}>{h.returned_on || "—"}</span>
              <span style={{ color: "var(--kr-fg-primary)" }}>{h.delivered_state || "—"}</span>
              <span style={{ color: h.returned_state ? "var(--kr-fg-primary)" : "var(--kr-fg-subtle)" }}>{h.returned_state || "—"}</span>
              <span style={{ color: "var(--kr-fg-muted)" }}>{h.accessories || "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

function TicketsTab({ rows, nav }) {
  if (!rows.length) return <Card><p style={{ fontSize: 13, color: "var(--kr-fg-muted)", margin: 0 }}>Este equipo no tiene incidencias registradas.</p></Card>;
  return (
    <Card padded={false}>
      <div style={{ padding: "14px 18px" }}><SupSection eyebrow="Soporte del equipo" title={`${rows.length} tickets asociados`} /></div>
      {rows.map((t, i) => (
        <div key={t.id} onClick={() => nav.go("ticket", { code: t.id })} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderTop: "1px solid var(--kr-border-subtle)", cursor: "pointer" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{t.title}</div>
            <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{t.number}</div>
          </div>
          <StatusBadge status={t.status} />
        </div>
      ))}
    </Card>
  );
}

function Mantenimientos({ rows, onAdd, busy }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ performed_on: "", type: "", performed_by: "", note: "" });
  const submit = () => { if (f.type.trim()) onAdd({ performed_on: f.performed_on || null, type: f.type.trim(), performed_by: f.performed_by.trim() || null, note: f.note.trim() || null }); };
  return (
    <Card>
      <SupSection eyebrow="Mantenimiento" title="Registro de mantenimientos" right={<Button variant="secondary" size="sm" icon="Plus" onClick={() => setOpen((o) => !o)}>Registrar</Button>} />
      {open && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14, padding: 14, border: "1px solid var(--kr-border-subtle)", borderRadius: 10, background: "var(--kr-neutral-25)" }}>
          <Field label="Tipo" required><Input value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} placeholder="Limpieza, reparación…" /></Field>
          <Field label="Fecha"><Input type="date" value={f.performed_on} onChange={(e) => setF({ ...f, performed_on: e.target.value })} /></Field>
          <Field label="Realizado por"><Input value={f.performed_by} onChange={(e) => setF({ ...f, performed_by: e.target.value })} placeholder="Técnico" /></Field>
          <Field label="Nota"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Detalle" /></Field>
          <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
            <Button variant="primary" size="sm" icon="Check" disabled={!f.type.trim() || busy} onClick={submit}>Guardar</Button>
          </div>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 14 }}>
        {rows.length ? rows.map((m, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 14px", border: "1px solid var(--kr-border-subtle)", borderRadius: 10, background: "var(--kr-neutral-25)" }}>
            <span style={{ width: 34, height: 34, borderRadius: 8, background: "var(--kr-status-pending-bg)", color: "var(--kr-status-pending-fg)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon name="wrench" size={16} /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{m.type}</div>
              <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>{[m.note, m.performed_by].filter(Boolean).join(" · ") || "—"}</div>
            </div>
            <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-muted)" }}>{m.performed_on || "—"}</span>
          </div>
        )) : <p style={{ fontSize: 13, color: "var(--kr-fg-subtle)", margin: 0 }}>Sin mantenimientos registrados.</p>}
      </div>
    </Card>
  );
}

function Documentos({ rows }) {
  return (
    <Card>
      <SupSection eyebrow="Documentación" title="Documentos del activo" right={<Badge tone="neutral" variant="soft">Subida próximamente</Badge>} />
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
        {rows.length ? rows.map((d) => (
          <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 14px", border: "1px solid var(--kr-border-subtle)", borderRadius: 10 }}>
            <span style={{ width: 34, height: 42, borderRadius: 4, background: "var(--kr-status-danger-bg)", color: "var(--kr-danger-700)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>DOC</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)" }}>{d.filename}</div>
              <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{[d.tag, d.size ? `${Math.round(d.size / 1024)} KB` : null].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
        )) : <p style={{ fontSize: 13, color: "var(--kr-fg-subtle)", margin: 0 }}>Sin documentos. La subida de ficheros se habilitará en breve.</p>}
      </div>
    </Card>
  );
}

function Auditoria({ rows }) {
  return (
    <Card>
      <SupSection eyebrow="Trazabilidad completa" title="Auditoría" />
      <div style={{ position: "relative", marginTop: 14 }}>
        <span style={{ position: "absolute", left: 13, top: 10, bottom: 10, width: 1, background: "var(--kr-border-subtle)" }} />
        {rows.length ? rows.map((l, i) => (
          <div key={i} style={{ display: "flex", gap: 14, padding: "9px 0", position: "relative" }}>
            <span style={{ width: 26, height: 26, borderRadius: 999, background: "#fff", border: "1px solid var(--kr-border)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--kr-fg-muted)", zIndex: 1, flexShrink: 0 }}><Icon name={auditIcon(l.type)} size={12} /></span>
            <div style={{ flex: 1 }}><span style={{ fontSize: 13, color: "var(--kr-fg-strong)" }}>{auditLabel(l.type)}</span></div>
            <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 11, color: "var(--kr-fg-muted)" }}>{fmtDt(l.created_at)}</span>
          </div>
        )) : <p style={{ fontSize: 13, color: "var(--kr-fg-subtle)", margin: 0 }}>Sin actividad.</p>}
      </div>
    </Card>
  );
}

function AssignPanel({ onSubmit, onCancel, busy }) {
  const [employees, setEmployees] = useState([]);
  const [f, setF] = useState({ employee_profile_id: "", assigned_on: "", delivered_state: "", accessories: "" });
  useEffect(() => { getEmployees().then((r) => setEmployees(unwrap(r) || [])).catch(() => {}); }, []);
  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--kr-border-subtle)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12 }}>
        <Field label="Usuario" required><SupSelect value={f.employee_profile_id} onChange={(v) => setF({ ...f, employee_profile_id: v })} placeholder="Selecciona empleado…" options={employees.map((e) => ({ value: e.id, label: empName(e) }))} /></Field>
        <Field label="Fecha de entrega"><Input type="date" value={f.assigned_on} onChange={(e) => setF({ ...f, assigned_on: e.target.value })} /></Field>
        <Field label="Estado al entregar"><Input value={f.delivered_state} onChange={(e) => setF({ ...f, delivered_state: e.target.value })} placeholder="Nuevo, buen estado…" /></Field>
        <Field label="Accesorios"><Input value={f.accessories} onChange={(e) => setF({ ...f, accessories: e.target.value })} placeholder="Cargador, funda…" /></Field>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button variant="secondary" size="sm" onClick={onCancel}>Cancelar</Button>
        <Button variant="primary" size="sm" icon="UserPlus" disabled={!f.employee_profile_id || busy} onClick={() => onSubmit({ employee_profile_id: f.employee_profile_id, assigned_on: f.assigned_on || null, delivered_state: f.delivered_state.trim() || null, accessories: f.accessories.trim() || null })}>Asignar</Button>
      </div>
    </div>
  );
}

function ReturnPanel({ onSubmit, onCancel, busy }) {
  const [f, setF] = useState({ returned_on: "", returned_state: "", to_status: "disponible" });
  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--kr-border-subtle)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        <Field label="Fecha de devolución"><Input type="date" value={f.returned_on} onChange={(e) => setF({ ...f, returned_on: e.target.value })} /></Field>
        <Field label="Estado al devolver"><Input value={f.returned_state} onChange={(e) => setF({ ...f, returned_state: e.target.value })} placeholder="Buen estado…" /></Field>
        <Field label="Nuevo estado"><SupSelect value={f.to_status} onChange={(v) => setF({ ...f, to_status: v })} options={Object.keys(ASTATUS).map((s) => ({ value: s, label: ASTATUS[s].label }))} /></Field>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <Button variant="secondary" size="sm" onClick={onCancel}>Cancelar</Button>
        <Button variant="primary" size="sm" icon="RefreshCw" disabled={busy} onClick={() => onSubmit({ returned_on: f.returned_on || null, returned_state: f.returned_state.trim() || null, to_status: f.to_status })}>Registrar devolución</Button>
      </div>
    </div>
  );
}

function auditIcon(type) {
  return { created: "plus", assigned: "users", returned: "refresh", status_changed: "refresh", updated: "edit", maintenance: "wrench" }[type] || "more";
}
function auditLabel(type) {
  return { created: "Activo dado de alta", assigned: "Asignado a un usuario", returned: "Devolución registrada", status_changed: "Cambio de estado", updated: "Activo actualizado", maintenance: "Mantenimiento registrado" }[type] || type;
}

export default function SoporteActivoPage() {
  const { id } = useParams();
  return <SoporteActivo key={id} />;
}
